import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { getPreguntas } from "../services/preguntasService";
import { obtenerEdadEstudiante } from "../utils/edadUsuario";
import {
  evaluarRespuesta,
  filtrarPreguntasPorEdad,
  indicesOpcionesCorrectas,
  normalizarListaPreguntas,
  preguntaEsJugable,
  seleccionarAleatorias,
} from "../utils/cuestionarioCurso";
import { logAxiosError } from "../utils/logAxiosError";
import Swal from "sweetalert2";

const clavePregunta = (p, index) => (p.idPregunta != null ? String(p.idPregunta) : `idx-${index}`);

function partesAutocompletar(texto) {
  return String(texto || "").split("_____");
}

const StudentCourseQuiz = ({
  idCurso,
  tituloCurso,
  cantidadPreguntas = 10,
  porcentajeAprobacion = 70,
  user,
  onFinalizarCurso,
  onSalir,
}) => {
  const cantidadObjetivo =
    Number.isFinite(Number(cantidadPreguntas)) && Number(cantidadPreguntas) > 0
      ? Number(cantidadPreguntas)
      : 10;
  const porcentajeObjetivo =
    Number.isFinite(Number(porcentajeAprobacion)) &&
    Number(porcentajeAprobacion) >= 1 &&
    Number(porcentajeAprobacion) <= 100
      ? Number(porcentajeAprobacion)
      : 70;
  const edad = useMemo(() => obtenerEdadEstudiante(user), [user]);
  const [fase, setFase] = useState("cargando");
  const [mensajeInfo, setMensajeInfo] = useState("");
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [resultados, setResultados] = useState(null);
  const [guardandoFinalizacion, setGuardandoFinalizacion] = useState(false);

  useEffect(() => {
    let cancel = false;
    const run = async () => {
      setFase("cargando");
      setResultados(null);
      setRespuestas({});
      try {
        const raw = await getPreguntas({ idCurso: Number(idCurso) });
        if (cancel) return;
        const todas = normalizarListaPreguntas(raw).filter(preguntaEsJugable);
        const porEdad = filtrarPreguntasPorEdad(todas, edad);
        if (edad == null) {
          setMensajeInfo(
            "No encontramos tu fecha de nacimiento en la sesión; se muestran preguntas de todos los rangos de edad."
          );
        } else {
          setMensajeInfo(`Preguntas filtradas para tu edad (${edad} años).`);
        }
        const muestra = seleccionarAleatorias(porEdad, cantidadObjetivo);
        setPreguntas(muestra);
        if (muestra.length === 0) {
          setFase("vacio");
        } else {
          setFase("respondiendo");
        }
      } catch (e) {
        logAxiosError("Estudiante: getPreguntas cuestionario", e);
        if (!cancel) setFase("error");
      }
    };
    run();
    return () => {
      cancel = true;
    };
  }, [idCurso, edad, cantidadObjetivo]);

  const setOpcionUnica = useCallback((key, index) => {
    setRespuestas((prev) => ({ ...prev, [key]: { indices: [index] } }));
  }, []);

  const toggleOpcionMultiple = useCallback((key, index) => {
    setRespuestas((prev) => {
      const cur = prev[key]?.indices ? [...prev[key].indices] : [];
      const pos = cur.indexOf(index);
      if (pos >= 0) cur.splice(pos, 1);
      else cur.push(index);
      return { ...prev, [key]: { indices: cur.sort((a, b) => a - b) } };
    });
  }, []);

  const setBlank = useCallback((key, blankIndex, value, blankCount) => {
    setRespuestas((prev) => {
      const blanks = prev[key]?.blanks ? [...prev[key].blanks] : Array(blankCount).fill("");
      blanks[blankIndex] = value;
      return { ...prev, [key]: { blanks } };
    });
  }, []);

  const validarCompletas = () => {
    for (let i = 0; i < preguntas.length; i++) {
      const p = preguntas[i];
      const key = clavePregunta(p, i);
      const r = respuestas[key];
      if (p.tipo === "autocompletar") {
        const n = (p.pregunta.match(/_____/g) || []).length;
        const blanks = r?.blanks || [];
        if (blanks.length < n || blanks.some((b) => !String(b || "").trim())) {
          return false;
        }
      } else {
        if (!r?.indices?.length) return false;
      }
    }
    return true;
  };

  const enviar = async () => {
    if (!validarCompletas()) {
      Swal.fire({
        icon: "info",
        title: "Faltan respuestas",
        text: "Responde todas las preguntas antes de ver la puntuación.",
        confirmButtonColor: "#113253",
      });
      return;
    }
    let correctas = 0;
    const detalle = preguntas.map((p, i) => {
      const key = clavePregunta(p, i);
      const r = respuestas[key];
      const payload =
        p.tipo === "autocompletar" ? { blanks: r.blanks } : { indices: r.indices };
      const { ok } = evaluarRespuesta(p, payload);
      if (ok) correctas += 1;
      return { pregunta: p, ok, payload };
    });
    const total = preguntas.length;
    const porcentaje = total > 0 ? Math.round((correctas / total) * 100) : 0;
    const aprobo = porcentaje >= porcentajeObjetivo;
    setGuardandoFinalizacion(true);
    let redirectToCertificate = false;
    try {
      if (onFinalizarCurso) {
        const result = await onFinalizarCurso({
          ultimaCalificacion: porcentaje,
          aprobo,
          variablesSeguimiento: {
            correctas,
            total,
            porcentaje,
            porcentajeAprobacion: porcentajeObjetivo,
            cantidadPreguntasObjetivo: cantidadObjetivo,
          },
        });
        redirectToCertificate = Boolean(result?.redirectToCertificate);
      }
    } catch (e) {
      logAxiosError(`Estudiante: registrar finalización curso ${idCurso}`, e);
      Swal.fire({
        icon: "warning",
        title: "Resultado local guardado",
        text: "No se pudo registrar la finalización en servidor. Intenta nuevamente.",
        confirmButtonColor: "#113253",
      });
    } finally {
      setGuardandoFinalizacion(false);
    }
    if (redirectToCertificate) return;
    setResultados({ correctas, total, detalle });
    setFase("resultados");
  };

  if (fase === "cargando" || fase === "error") {
    return (
      <Box py={6} textAlign="center">
        {fase === "cargando" ? (
          <>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">Preparando cuestionario…</Typography>
          </>
        ) : (
          <Typography color="error">No se pudieron cargar las preguntas. Intenta más tarde.</Typography>
        )}
        <Button sx={{ mt: 2 }} onClick={onSalir}>
          Volver a cursos
        </Button>
      </Box>
    );
  }

  if (fase === "vacio") {
    return (
      <Box py={4}>
        <Typography sx={{ mb: 2 }}>
          No hay preguntas disponibles para este curso
          {edad != null ? ` y tu edad (${edad} años)` : ""}.
        </Typography>
        <Button variant="contained" onClick={onSalir}>
          Volver a cursos
        </Button>
      </Box>
    );
  }

  if (fase === "resultados" && resultados) {
    const pct = Math.round((resultados.correctas / resultados.total) * 100);
    const aprobado = pct >= porcentajeObjetivo;
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Resultado: {tituloCurso}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {resultados.correctas} / {resultados.total}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Puntuación: {pct}%
        </Typography>
        <Typography
          sx={{
            mb: 2,
            fontWeight: 700,
            color: aprobado ? "success.main" : "error.main",
          }}
        >
          {aprobado ? "Aprobado" : "Reprobado"} · Mínimo requerido: {porcentajeObjetivo}%
        </Typography>
        <LinearProgress variant="determinate" value={pct} sx={{ height: 10, borderRadius: 1, mb: 3 }} />

        {resultados.detalle.map((row, i) => (
          <Card key={clavePregunta(row.pregunta, i)} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                {row.ok ? (
                  <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
                ) : (
                  <CancelIcon color="error" fontSize="small" sx={{ mt: 0.25 }} />
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {i + 1}. {row.pregunta.pregunta}
                </Typography>
              </Box>
              {!row.ok && (
                <Typography variant="body2" color="text.secondary" sx={{ pl: 3.5 }}>
                  {row.pregunta.tipo === "autocompletar" ? (
                    <>
                      Respuestas correctas:{" "}
                      {row.pregunta.opciones.map((o) => o.respuesta).join(" · ")}
                    </>
                  ) : (
                    <>
                      Correcta(s):{" "}
                      {indicesOpcionesCorrectas(row.pregunta)
                        .map((idx) => row.pregunta.opciones[idx]?.respuesta)
                        .filter(Boolean)
                        .join(", ")}
                    </>
                  )}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}

        <Button variant="contained" fullWidth onClick={onSalir} sx={{ mt: 1 }}>
          Volver a cursos
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
        Cuestionario final: {tituloCurso}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {mensajeInfo} Seleccionamos hasta {cantidadObjetivo} preguntas al azar. El porcentaje mínimo para aprobar es {porcentajeObjetivo}%.
      </Typography>

      {preguntas.map((p, i) => {
        const key = clavePregunta(p, i);
        return (
          <Card key={key} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {i + 1}. {p.pregunta}
              </Typography>
              {p.descripcion ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {p.descripcion}
                </Typography>
              ) : null}

              {p.tipo === "autocompletar" ? (
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} sx={{ mt: 1 }}>
                  {partesAutocompletar(p.pregunta).map((part, pi, arr) => (
                    <React.Fragment key={`${key}-p-${pi}`}>
                      <Typography component="span" variant="body2">
                        {part}
                      </Typography>
                      {pi < arr.length - 1 ? (
                        <TextField
                          size="small"
                          sx={{ width: 140 }}
                          value={respuestas[key]?.blanks?.[pi] ?? ""}
                          onChange={(e) =>
                            setBlank(key, pi, e.target.value, arr.length - 1)
                          }
                        />
                      ) : null}
                    </React.Fragment>
                  ))}
                </Box>
              ) : p.tipo === "multiple" ? (
                <FormGroup>
                  {p.opciones.map((op, oi) => (
                    <FormControlLabel
                      key={`${key}-op-${oi}`}
                      control={
                        <Checkbox
                          checked={Boolean(respuestas[key]?.indices?.includes(oi))}
                          onChange={() => toggleOpcionMultiple(key, oi)}
                        />
                      }
                      label={op.respuesta}
                    />
                  ))}
                </FormGroup>
              ) : (
                <FormControl component="fieldset" variant="standard">
                  <RadioGroup
                    value={respuestas[key]?.indices?.[0] ?? ""}
                    onChange={(e) => setOpcionUnica(key, Number(e.target.value))}
                  >
                    {p.opciones.map((op, oi) => (
                      <FormControlLabel
                        key={`${key}-op-${oi}`}
                        value={oi}
                        control={<Radio />}
                        label={op.respuesta}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Divider sx={{ my: 2 }} />

      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
        <Button variant="outlined" fullWidth onClick={onSalir}>
          Salir sin enviar
        </Button>
        <Button variant="contained" fullWidth onClick={enviar} disabled={guardandoFinalizacion}>
          {guardandoFinalizacion ? "Registrando resultado..." : "Ver puntuación"}
        </Button>
      </Box>
      {!validarCompletas() ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Responde todas las preguntas para calcular la puntuación.
        </Typography>
      ) : null}
    </Box>
  );
};

export default StudentCourseQuiz;
